import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import {
  fromSeoUrl,
  toSeoUrl,
  toSingular,
  toPlural,
} from 'helpers/HelperFunctions';
import toSentenceCase from 'helpers/sentenceCase';
import {
  MAX_URL_PAGE,
  buildJobsBatchUrl,
  clampJobsPage,
} from '../jobsPagination';
import getJobsCanonical from '../getJobsCanonical';

import styles from './ProjectsHeader.module.scss';

const ProjectsHeader = ({
  filter = null,
  paid,
  getSelectedRoleName,
  config,
  loggedIn,
  batchPage = 1,
  moreToLoad = false,
  locationPathname = '',
  locationSearch = '',
  children = null,
}) => {
  const selectedRoleName = getSelectedRoleName() || '';
  const locationString =
    filter && filter.city
      ? ' in ' + fromSeoUrl(filter.city) + ', ' + fromSeoUrl(filter.country)
      : filter && filter.country
      ? ' in ' + fromSeoUrl(filter.country)
      : '';

  const description = `Find & apply for work on ${
    selectedRoleName ? toSingular(selectedRoleName.toLowerCase()) : 'freelance'
  } projects${
    locationString ? locationString : ''
  }. Find the best remote and freelance jobs from clients around the world on Twine.`;

  const titleRole = selectedRoleName
    ? toSentenceCase(toSingular(selectedRoleName))
    : '';
  const title = paid
    ? `${titleRole || 'Freelance'} Jobs ${
        locationString ? locationString : ' With Top Global Clients'
      } | Twine`
    : `${titleRole || 'Project'} Collaborations | Twine`;

  const getCollaborateLink = () => {
    let link = config.baseUrl;

    link += '/collaborate';

    if (filter) {
      if (selectedRoleName !== '') {
        link += `/${toSeoUrl(toPlural(selectedRoleName))}`;
      }
      if (filter.country) {
        link += filter.city
          ? `/in/${toSeoUrl(filter.country)}/${toSeoUrl(filter.city)}`
          : `/in/${toSeoUrl(filter.country)}`;
      }
    }

    return link;
  };

  const baseUrl = (config.baseUrl || '').replace(/\/$/, '');
  const { canonical } = paid
    ? getJobsCanonical({
        role: (filter && filter.name) || null,
        country: (filter && filter.country) || null,
        city: (filter && filter.city) || null,
        query: locationSearch,
        baseUrl,
      })
    : { canonical: getCollaborateLink() };

  const pathname =
    locationPathname ||
    canonical.replace(baseUrl, '') ||
    (paid ? '/jobs' : '/collaborate');
  const absoluteBatchUrl = (page) =>
    `${baseUrl}${buildJobsBatchUrl(pathname, locationSearch, page)}`;

  // SEO prev/next use the crawlable URL page (capped at MAX_URL_PAGE)
  const urlBatchPage = clampJobsPage(batchPage);

  const helmetLinks = [
    {
      rel: 'canonical',
      href: canonical,
    },
  ];

  if (urlBatchPage > 1) {
    helmetLinks.push({
      rel: 'prev',
      href: absoluteBatchUrl(urlBatchPage - 1),
    });
  }

  if (moreToLoad && urlBatchPage < MAX_URL_PAGE) {
    helmetLinks.push({
      rel: 'next',
      href: absoluteBatchUrl(urlBatchPage + 1),
    });
  }

  const helmetMeta = [
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    {
      property: 'og:url',
      content: canonical,
    },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
  ];

  const roleLabelRaw = selectedRoleName
    ? toSingular(selectedRoleName)
    : filter && filter.name
    ? toSingular(fromSeoUrl(filter.name))
    : '';
  const roleLabel = roleLabelRaw ? toSentenceCase(roleLabelRaw) : '';
  const countryLabel =
    filter && filter.country ? fromSeoUrl(filter.country) : '';

  const showBreadcrumb = paid && (roleLabel || countryLabel);

  const h1LocationSuffix =
    paid && !roleLabel && locationString ? locationString : '';
  const h1Text = paid
    ? roleLabel
      ? `${roleLabel} freelance and remote jobs${h1LocationSuffix}`
      : `Freelance and remote jobs${h1LocationSuffix}`
    : `${roleLabel ? `${roleLabel} ` : ''}Collaboration board`;

  return (
    <div>
      <Helmet title={title} link={helmetLinks} meta={helmetMeta} />

      {showBreadcrumb && (
        <Breadcrumbs
          className={styles.breadcrumbs}
          data-testid="jobs-breadcrumb"
        >
          <Link to="/jobs">Jobs</Link>
          {roleLabel ? (
            <span>{`${roleLabel} jobs`}</span>
          ) : (
            <span>{`Jobs${locationString}`}</span>
          )}
          {roleLabel && countryLabel ? <span>{countryLabel}</span> : null}
        </Breadcrumbs>
      )}

      <h1 className={styles.title}>{h1Text}</h1>
      {!loggedIn && (
        <>
          <h2 className={styles.subtitle}>
            {paid ? 'Find and apply for work on freelance ' : 'Find '}
            {filter && roleLabel
              ? `${toSingular(roleLabel.toLowerCase())} `
              : ''}
            {paid
              ? 'projects' + (locationString ? locationString : '')
              : 'project collaborations'}
          </h2>
          {paid ? (
            <h3 className={styles.description}>
              Find the best{' '}
              {filter && roleLabel
                ? `freelance ${toSingular(
                    roleLabel.toLowerCase()
                  )} jobs and remote 
              ${toSingular(roleLabel.toLowerCase())} jobs `
                : 'remote and freelance jobs '}
              from clients
              {locationString ? locationString : ' around the world'}. Search
              for projects matching your
              {roleLabel ? ` ${toSingular(roleLabel.toLowerCase())}` : ''}{' '}
              skills at top companies. Apply today.
            </h3>
          ) : (
            <h3 className={styles.description}>
              Browse{' '}
              {filter &&
                roleLabel &&
                `for ${toSingular(roleLabel.toLowerCase())}`}{' '}
              collaboration posts
            </h3>
          )}
        </>
      )}
      {children}
    </div>
  );
};

ProjectsHeader.propTypes = {
  filter: PropTypes.object,
  paid: PropTypes.bool,
  getSelectedRoleName: PropTypes.func,
  config: PropTypes.object,
  loggedIn: PropTypes.bool,
  batchPage: PropTypes.number,
  moreToLoad: PropTypes.bool,
  locationPathname: PropTypes.string,
  locationSearch: PropTypes.string,
  children: PropTypes.node,
};

ProjectsHeader.defaultProps = {
  filter: null,
  batchPage: 1,
  moreToLoad: false,
  locationPathname: '',
  locationSearch: '',
  children: null,
};

export default ProjectsHeader;
